import React from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="layout">
      <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h2>{sidebarCollapsed ? 'OW' : 'Orian Workflow Platform'}</h2>
          {!sidebarCollapsed && (
            <div className="user-info">
              <div className="user-avatar">{user?.username.charAt(0).toUpperCase()}</div>
              <div className="user-details">
                <div className="user-name">{user?.username}</div>
                <div className="user-role">{user?.role}</div>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="user-info-collapsed">
              <div className="user-avatar">{user?.username.charAt(0).toUpperCase()}</div>
            </div>
          )}
        </div>

        <div className="nav-menu">
          <Link
            to="/dashboard"
            className={isActive('/dashboard') && !isActive('/dashboard/') ? 'nav-item active' : 'nav-item'}
            title={sidebarCollapsed ? 'Dashboard' : ''}
          >
            <span className="nav-icon">📊</span>
            {!sidebarCollapsed && <span>Dashboard</span>}
          </Link>

          <Link
            to="/agents"
            className={isActive('/agents') ? 'nav-item active' : 'nav-item'}
            title={sidebarCollapsed ? 'Agent Catalog' : ''}
          >
            <span className="nav-icon">🤖</span>
            {!sidebarCollapsed && <span>Agent Catalog</span>}
          </Link>

          <Link
            to="/workflows"
            className={isActive('/workflows') ? 'nav-item active' : 'nav-item'}
            title={sidebarCollapsed ? 'Saved Workflows' : ''}
          >
            <span className="nav-icon">📋</span>
            {!sidebarCollapsed && <span>Saved Workflows</span>}
          </Link>

          {/* Designer - Only for creators and admins */}
          {(user?.role === 'admin' || user?.role === 'workflow_creator') && (
            <Link
              to="/designer"
              className={isActive('/designer') ? 'nav-item active' : 'nav-item'}
              title={sidebarCollapsed ? 'Workflow Designer' : ''}
            >
              <span className="nav-icon">🎨</span>
              {!sidebarCollapsed && <span>Workflow Designer</span>}
            </Link>
          )}

          <Link
            to="/executions"
            className={isActive('/executions') ? 'nav-item active' : 'nav-item'}
            title={sidebarCollapsed ? 'Executions' : ''}
          >
            <span className="nav-icon">▶️</span>
            {!sidebarCollapsed && <span>Executions</span>}
          </Link>

          {/* Admin - Only for admins */}
          {user?.role === 'admin' && (
            <>
              <Link
                to="/admin"
                className={isActive('/admin') && !isActive('/admin/users') ? 'nav-item active' : 'nav-item'}
                title={sidebarCollapsed ? 'Administration' : ''}
              >
                <span className="nav-icon">⚙️</span>
                {!sidebarCollapsed && <span>Administration</span>}
              </Link>
              <Link
                to="/admin/users"
                className={isActive('/admin/users') ? 'nav-item active' : 'nav-item'}
                title={sidebarCollapsed ? 'User Management' : ''}
              >
                <span className="nav-icon">👥</span>
                {!sidebarCollapsed && <span>User Management</span>}
              </Link>
            </>
          )}
        </div>

        <div className="sidebar-footer">
          {!sidebarCollapsed && (
            <button onClick={handleLogout} className="logout-button">
              <span className="nav-icon">🚪</span>
              <span>Logout</span>
            </button>
          )}
          {sidebarCollapsed && (
            <button onClick={handleLogout} className="logout-button" title="Logout">
              <span className="nav-icon">🚪</span>
            </button>
          )}
          {!sidebarCollapsed && (
            <div className="xoriant-branding">
              <span className="branding-text">Powered by</span>
              <span className="branding-company">Xoriant</span>
            </div>
          )}
        </div>
      </nav>

      {/* Sidebar Toggle Button */}
      <button
        className={`sidebar-toggle ${sidebarCollapsed ? 'collapsed' : ''}`}
        onClick={toggleSidebar}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? '▶' : '◀'}
      </button>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;

